// Who may manage whom on the Staff page (SM-852 / SM-857 / SM-858).
//
// broker-service decides every request: STAFF_MANAGE reaches the member's
// node and its descendants (PO 2026-10-08), the caller is judged by its
// STRONGEST covering membership, and the grant matrix is the same on a
// descendant as on its own node — OWNER manages every role, ADMINISTRATOR
// manages FINANCE, TRADER and VIEWER, nobody invites themselves. This
// module is the portal's reading of those rules, so the Invite dialog
// offers only grantable roles and a row shows only the actions the server
// would accept. A refusal still comes from the server (error catalog).
import { activeMemberships } from "./capabilities.ts";
import { STAFF_ROLES } from "./types.ts";
import type { AccountScope, StaffListParams, StaffMeResponse, StaffMembershipItem, StaffRole } from "./types.ts";

/** Role rank, strongest first (broker-service domain.RoleRank). */
const RANK: Record<StaffRole, number> = { OWNER: 5, ADMINISTRATOR: 4, FINANCE: 3, VIEWER: 2, TRADER: 1 };

/** The grant matrix (broker-service domain.CanManage). */
const MANAGEABLE: Partial<Record<StaffRole, readonly StaffRole[]>> = {
  OWNER: STAFF_ROLES,
  ADMINISTRATOR: ["FINANCE", "TRADER", "VIEWER"],
};

/** The roles a caller holding `actorRole` may grant, change or remove, in STAFF_ROLES order. */
export function grantableRoles(actorRole: StaffRole | undefined): StaffRole[] {
  if (!actorRole) return [];
  const allowed = MANAGEABLE[actorRole] ?? [];
  return STAFF_ROLES.filter((r) => allowed.includes(r));
}

export function canManageRole(actorRole: StaffRole | undefined, targetRole: StaffRole): boolean {
  return grantableRoles(actorRole).includes(targetRole);
}

/**
 * The strongest role among the caller's ACTIVE memberships that sit on one
 * of `coveringNodeIds` (the selected node's breadcrumb from the covering
 * membership down, and the row's own node) and hold STAFF_MANAGE.
 */
export function manageRoleOn(me: StaffMeResponse | null | undefined, coveringNodeIds: readonly string[]): StaffRole | undefined {
  let best: StaffRole | undefined;
  for (const m of activeMemberships(me)) {
    if (!coveringNodeIds.includes(m.broker.broker_id)) continue;
    if (!(m.capabilities ?? []).includes("STAFF_MANAGE")) continue;
    if (!best || RANK[m.membership.role] > RANK[best]) best = m.membership.role;
  }
  return best;
}

/**
 * Whether a row's Suspend / Resume / Change role / Remove are offered: the
 * caller manages staff on the row's node (through the selected node's path
 * or a membership on the row's node) and its role may manage the row's.
 */
export function canActOnRow(
  me: StaffMeResponse | null | undefined,
  selectedPathIds: readonly string[],
  row: Pick<StaffMembershipItem, "broker_id" | "role" | "status">,
): boolean {
  if (row.status === "REMOVED") return false;
  return canManageRole(manageRoleOn(me, [...selectedPathIds, row.broker_id]), row.role);
}

/** A staff actor never invites itself (BROKER_STAFF_ESCALATION server-side). */
/** Whether the invite names the caller's own email (compared as the server does: trimmed, lower-case). */
export function isSelfInvite(email: string, sessionEmail: string | undefined): boolean {
  return !!sessionEmail && email.trim().toLowerCase() === sessionEmail.trim().toLowerCase();
}

/**
 * A plausible address, the server's own rule (BROKER_STAFF_INVITE_EMAIL_INVALID
 * otherwise): one @ with something before it, a dotted domain, no spaces.
 */
export function isPlausibleEmail(raw: string): boolean {
  const e = raw.trim().toLowerCase();
  if (e === "" || e.length > 254 || /[\s<>,;"]/.test(e)) return false;
  const at = e.indexOf("@");
  if (at < 1 || at !== e.lastIndexOf("@") || at === e.length - 1) return false;
  const host = e.slice(at + 1);
  return host.includes(".") && !host.startsWith(".") && !host.endsWith(".") && !host.includes("..");
}

/** A member as a screen names them: the staff account's name, else its email, else the raw id (a pre-SM-968 row). */
export function memberLabel(m: { display_name?: string; email?: string; user_id: string }): string {
  return m.display_name?.trim() || m.email || m.user_id;
}

/** An invitation whose person has not set a password yet: "Pending sign-up", and Resend invite is offered. */
export function isPendingSignUp(m: { status: string; account_status?: string }): boolean {
  return m.status === "INVITED" && m.account_status === "PENDING";
}

// --- the Staff page's URL state ----------------------------------------------------

export interface StaffSearch {
  /** "subtree" = Include sub-brokers; absent = this node only. */
  staffScope?: "subtree";
  /** Per-person access: one user's memberships. */
  staffUser?: string;
}

// broker-service domain.ValidateID: no whitespace or control characters, at most 128.
const ID_RE = /^[^\s\x00-\x1f\x7f]{1,128}$/;

export function parseStaffSearch(s: Record<string, unknown>): StaffSearch {
  return {
    staffScope: s.staffScope === "subtree" ? "subtree" : undefined,
    staffUser: typeof s.staffUser === "string" && ID_RE.test(s.staffUser.trim()) ? s.staffUser.trim() : undefined,
  };
}

/** The list call for the page's URL state. */
export function staffListParams(
  search: StaffSearch,
  page: { includeRemoved: boolean; offset: number; limit: number },
): StaffListParams {
  const scope: AccountScope | undefined = search.staffScope === "subtree" ? "subtree" : undefined;
  return {
    scope,
    user_id: search.staffUser,
    include_removed: page.includeRemoved || undefined,
    offset: page.offset,
    limit: page.limit,
  };
}

// --- the Invite dialog's per-node run -----------------------------------------------

export interface InviteTarget {
  nodeId: string;
  name: string;
}

export type InviteOutcome =
  | (InviteTarget & { state: "pending" })
  | (InviteTarget & { state: "ok"; auditIds: string[]; membershipId: string })
  | (InviteTarget & { state: "failed"; error: unknown });

/** The targets a "Retry failed" resubmits: only the failures, in their order. */
export function retryTargets(outcomes: readonly InviteOutcome[]): InviteTarget[] {
  return outcomes.filter((o) => o.state === "failed").map(({ nodeId, name }) => ({ nodeId, name }));
}

/** Merge a retry's outcomes over the previous run, keeping each node's place. */
export function mergeOutcomes(previous: readonly InviteOutcome[], next: readonly InviteOutcome[]): InviteOutcome[] {
  const byNode = new Map(next.map((o) => [o.nodeId, o]));
  return previous.map((o) => byNode.get(o.nodeId) ?? o);
}

/**
 * One POST /api/broker/{node}/staff per target, SEQUENTIALLY (each node
 * gets its own audit row; there is no batch endpoint on purpose). `send`
 * answers the audit ids and the membership; a refusal is recorded and the
 * run moves on. `onProgress` sees every intermediate state.
 */
export async function runInvites(
  targets: readonly InviteTarget[],
  send: (nodeId: string) => Promise<{ auditIds: string[]; membershipId: string }>,
  onProgress?: (outcomes: InviteOutcome[]) => void,
): Promise<InviteOutcome[]> {
  const out: InviteOutcome[] = targets.map((t) => ({ ...t, state: "pending" }));
  onProgress?.([...out]);
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i]!;
    try {
      const r = await send(t.nodeId);
      out[i] = { ...t, state: "ok", auditIds: r.auditIds, membershipId: r.membershipId };
    } catch (error) {
      out[i] = { ...t, state: "failed", error };
    }
    onProgress?.([...out]);
  }
  return out;
}
